import { useRemoteContent } from "@/lib/ContentContext";

const STATS_ICONS = [
  "ri-award-line",
  "ri-bookmark-line",
  "ri-team-line",
  "ri-global-line",
  "ri-trophy-line",
  "ri-star-line",
  "ri-building-line",
  "ri-shield-line",
];

const advantages = [
  {
    icon: "ri-microscope-line",
    zh: { title: "顶尖研发实力", desc: "拥有国家级实验室和仿真平台，整合高校与科研院所资源，持续推动核心技术突破。" },
    en: { title: "Top R&D Capability", desc: "National-level labs and simulation platforms, integrating academic and research resources for breakthrough innovations." },
  },
  {
    icon: "ri-shield-check-line",
    zh: { title: "严苛质量标准", desc: "遵循GJB、AS9100等航空行业质量体系，所有产品通过严格的适航与可靠性验证。" },
    en: { title: "Rigorous Quality Standards", desc: "Compliant with GJB and AS9100 standards, all products pass strict airworthiness and reliability certification." },
  },
  {
    icon: "ri-customer-service-2-line",
    zh: { title: "全程技术保障", desc: "提供从需求分析、设计研发、生产交付到售后服务的全生命周期技术支持与保障。" },
    en: { title: "Full Lifecycle Support", desc: "Comprehensive technical support from requirements analysis, R&D, delivery to after-sales service throughout product lifecycle." },
  },
];

interface AboutUsProps {
  lang: "zh" | "en";
}

export default function AboutUs({ lang }: AboutUsProps) {
  const siteContent = useRemoteContent();

  return (
    <section id="about" className="py-20 md:py-28 bg-xh-bg-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-500/30 bg-orange-500/5 mb-4">
            <i className="ri-building-2-line text-orange-400 text-sm" />
            <span className="text-orange-400 text-xs font-semibold tracking-widest uppercase">
              {lang === "zh" ? "关于我们" : "About Us"}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {lang === "zh" ? "公司简介" : "Company Profile"}
          </h2>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden w-full h-[320px] md:h-[400px]">
              <img
                src={siteContent.aboutImage}
                alt="About Us"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#090e1d]/60 to-transparent" />
            </div>
            <div className="absolute -bottom-5 -right-4 bg-xh-bg-card border border-cyan-500/30 rounded-xl p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cyan-500/20">
                  <i className="ri-medal-line text-cyan-400 text-lg" />
                </div>
                <div>
                  <p className="text-white text-xs font-bold">
                    {lang === "zh" ? "国家高新技术企业" : "National Hi-Tech Enterprise"}
                  </p>
                  <p className="text-gray-500 text-xs">MIIT Certified</p>
                </div>
              </div>
            </div>
          </div>

          {/* Text */}
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-5 leading-snug">
              {lang === "zh"
                ? "专注航空动力，服务国家航空产业战略"
                : "Dedicated to Aerospace Propulsion, Serving National Aviation Strategy"}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              {lang === "zh" ? siteContent.aboutZhDesc1 : siteContent.aboutEnDesc1}
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mb-8">
              {lang === "zh" ? siteContent.aboutZhDesc2 : siteContent.aboutEnDesc2}
            </p>

            {/* Advantages */}
            <div className="flex flex-col gap-4">
              {advantages.map((a, i) => {
                const t = lang === "zh" ? a.zh : a.en;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-cyan-500/10 flex-shrink-0 mt-0.5">
                      <i className={`${a.icon} text-cyan-400 text-sm`} />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold mb-0.5">{t.title}</p>
                      <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className={`grid gap-4 grid-cols-2 ${siteContent.aboutStats.length <= 2 ? "md:grid-cols-2" : siteContent.aboutStats.length === 3 ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
          {siteContent.aboutStats.map((s, i) => (
            <div
              key={i}
              className="text-center p-6 rounded-xl bg-xh-bg-card border border-white/5 hover:border-cyan-500/30 transition-all duration-300"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cyan-500/10 mx-auto mb-3">
                <i className={`${STATS_ICONS[i % STATS_ICONS.length]} text-cyan-400 text-lg`} />
              </div>
              <p className="text-2xl md:text-3xl font-bold text-white mb-1">{s.num}</p>
              <p className="text-gray-500 text-xs">{lang === "zh" ? s.zhLabel : s.enLabel}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
